import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPlans, createPlan, updatePlan, deletePlan, type Plan } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Plus, Edit2, Trash2, Crown } from "lucide-react";

const LIMIT_LABELS: Record<string, string> = {
  maxMenus: "Max Menu",
  maxItems: "Max Urun",
  maxCategories: "Max Kategori",
  maxImages: "Max Gorsel",
  maxQrCodes: "Max QR Kod",
  maxLanguages: "Max Dil",
};

const FEATURE_LABELS: Record<string, string> = {
  analytics: "Analitik",
  customTheme: "Tema Ozellestirme",
  customCss: "Custom CSS",
  removeBranding: "Branding Kaldirma",
  customDomain: "Custom Domain",
  pdfExport: "PDF Export",
  orderEnabled: "Siparis Alma",
  aiTranslate: "AI Ceviri",
  aiImageGeneration: "AI Gorsel Uretimi",
};

function PlanFormDialog({ open, onClose, plan }: { open: boolean; onClose: () => void; plan?: Plan }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<Plan>>({});

  useState(() => {
    if (plan) {
      setForm({ ...plan });
    } else {
      setForm({
        type: "",
        name: "",
        description: "",
        priceMonthly: 0,
        priceYearly: 0,
        limits: { maxMenus: 1, maxItems: 30, maxCategories: 5, maxImages: 10, maxQrCodes: 1, maxLanguages: 1 },
        features: { analytics: false, analyticsDays: 0, customTheme: false, customCss: false, removeBranding: false, customDomain: false, pdfExport: false, orderEnabled: true, aiTranslate: false, aiImageGeneration: false },
        welcomeCredits: 0,
        presets: ["default", "dark", "minimal"],
        isActive: true,
        sortOrder: 0,
      });
    }
  });

  const saveMutation = useMutation({
    mutationFn: () => plan ? updatePlan(plan._id, form) : createPlan(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      onClose();
    },
  });

  const updateLimits = (key: string, value: number) => {
    setForm({ ...form, limits: { ...form.limits!, [key]: value } });
  };

  const updateFeatures = (key: string, value: boolean | number) => {
    setForm({ ...form, features: { ...form.features!, [key]: value } });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{plan ? "Plani Duzenle" : "Yeni Plan"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }} className="space-y-4">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Plan Tipi (KEY)</Label>
              <Input value={form.type || ""} onChange={(e) => setForm({ ...form, type: e.target.value.toUpperCase() })} required disabled={!!plan} placeholder="ENTERPRISE" />
            </div>
            <div>
              <Label>Plan Adi</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Enterprise" />
            </div>
          </div>
          <div>
            <Label>Aciklama</Label>
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Aylik Fiyat ($)</Label>
              <Input type="number" min={0} step="0.01" value={form.priceMonthly ?? 0} onChange={(e) => setForm({ ...form, priceMonthly: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Yillik Fiyat ($)</Label>
              <Input type="number" min={0} step="0.01" value={form.priceYearly ?? 0} onChange={(e) => setForm({ ...form, priceYearly: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Aylik Fiyat (TRY)</Label>
              <Input type="number" min={0} step="0.01" value={form.priceTryMonthly ?? 0} onChange={(e) => setForm({ ...form, priceTryMonthly: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Yillik Fiyat (TRY)</Label>
              <Input type="number" min={0} step="0.01" value={form.priceTryYearly ?? 0} onChange={(e) => setForm({ ...form, priceTryYearly: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Hosgeldin AI Kredi</Label>
              <Input type="number" min={0} value={form.welcomeCredits ?? 0} onChange={(e) => setForm({ ...form, welcomeCredits: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Siralama</Label>
              <Input type="number" value={form.sortOrder ?? 0} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
            </div>
          </div>

          {/* Limits */}
          <div className="border-t pt-4">
            <Label className="text-base font-semibold">Limitler</Label>
            <p className="text-xs text-muted-foreground mb-3">-1 = Sinirsiz</p>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(LIMIT_LABELS).map(([key, label]) => (
                <div key={key}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    type="number"
                    min={-1}
                    value={(form.limits as Record<string, number>)?.[key] ?? 0}
                    onChange={(e) => updateLimits(key, Number(e.target.value))}
                    className="h-8"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="border-t pt-4">
            <Label className="text-base font-semibold">Ozellikler</Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <Switch
                    checked={(form.features as unknown as Record<string, boolean>)?.[key] ?? false}
                    onCheckedChange={(v) => updateFeatures(key, v)}
                  />
                  <Label className="text-sm">{label}</Label>
                </div>
              ))}
              <div>
                <Label className="text-xs">Analitik Gun</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.features?.analyticsDays ?? 0}
                  onChange={(e) => updateFeatures("analyticsDays", Number(e.target.value))}
                  className="h-8 w-24"
                />
              </div>
            </div>
          </div>

          {/* Active */}
          <div className="flex items-center gap-2 border-t pt-4">
            <Switch checked={form.isActive ?? true} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
            <Label>Aktif</Label>
          </div>

          <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Kaydediliyor..." : plan ? "Guncelle" : "Olustur"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PlansPage() {
  const qc = useQueryClient();
  const [editPlan, setEditPlan] = useState<Plan | undefined>();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: plans, isLoading } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: getPlans,
  });

  const delMutation = useMutation({
    mutationFn: deletePlan,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plans"] }),
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Plan Yonetimi</h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 size-4" />
          Yeni Plan
        </Button>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yukleniyor...</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans?.map((plan) => (
            <Card key={plan._id} className={!plan.isActive ? "opacity-50" : ""}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Crown className="size-5 text-primary" />
                    {plan.name}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="size-8" onClick={() => setEditPlan(plan)}>
                      <Edit2 className="size-4" />
                    </Button>
                    {plan.type !== "FREE" && (
                      <ConfirmDialog
                        trigger={<Button variant="ghost" size="icon" className="size-8"><Trash2 className="size-4 text-destructive" /></Button>}
                        title="Plani Sil"
                        description={`"${plan.name}" plani silinecek.`}
                        confirmLabel="Sil"
                        onConfirm={() => delMutation.mutate(plan._id)}
                      />
                    )}
                  </div>
                </div>
                <Badge variant="outline" className="w-fit">{plan.type}</Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-2xl font-bold text-primary">
                  {plan.priceMonthly > 0 ? `$${plan.priceMonthly}` : "Ucretsiz"}
                  {plan.priceMonthly > 0 && <span className="text-sm font-normal text-muted-foreground">/ay</span>}
                </div>
                {plan.description && <p className="text-sm text-muted-foreground">{plan.description}</p>}

                <div className="space-y-1 text-sm">
                  {Object.entries(LIMIT_LABELS).map(([key, label]) => {
                    const val = (plan.limits as Record<string, number>)[key];
                    return (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-medium">{val === -1 ? "∞" : val}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-1">
                  {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                    const enabled = (plan.features as unknown as Record<string, boolean>)[key];
                    if (!enabled) return null;
                    return <Badge key={key} variant="secondary" className="text-xs">{label}</Badge>;
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PlanFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {editPlan && <PlanFormDialog open={!!editPlan} onClose={() => setEditPlan(undefined)} plan={editPlan} />}
    </div>
  );
}
